/**
 * Uma planilha `.xlsx` a partir de uma tabela (#880; saiu de `clientes/planilha.ts`).
 *
 * As montagens devolvem a TABELA, com número como número; quem a transforma
 * em arquivo é o `baixar`. A exportação de clientes, os relatórios e a lista de
 * espera das automações usam daqui.
 */
import { TIPO_XLSX, xlsx, type Celula, type Tabela } from './xlsx';

export type { Celula, Tabela } from './xlsx';

/** `120000000000` micros → `120000`, em reais, como número que soma. */
function dinheiro(micros: number | null): number | null {
  if (micros === null) return null;
  return Math.round(micros / 10_000) / 100;
}

/**
 * Qualquer tabela: o cabeçalho e as linhas.
 *
 * Exportada para a lista de espera das automações, que é a segunda planilha do
 * produto.
 */
export function planilha(cabecalho: readonly string[], linhas: Tabela): Tabela {
  return [cabecalho, ...linhas];
}

/** Um bloco de uma planilha com várias tabelas: título, cabeçalho e linhas. */
export interface BlocoDaPlanilha {
  readonly titulo: string;
  readonly colunas: readonly string[];
  readonly linhas: Tabela;
}

/**
 * Várias tabelas numa aba só, para os relatórios (08/10/2026).
 *
 * No topo vai o que diz de onde são os números (relatório, período, de quem),
 * em pares rótulo e valor; depois cada bloco com o título numa linha própria,
 * o cabeçalho e as linhas, separados por uma linha vazia. Na ordem em que a
 * tela mostra.
 */
export function planilhaEmBlocos(
  topo: readonly (readonly [string, string])[],
  blocos: readonly BlocoDaPlanilha[],
): Tabela {
  const linhas: (readonly Celula[])[] = [...topo];
  for (const bloco of blocos) {
    linhas.push([], [bloco.titulo], bloco.colunas, ...bloco.linhas);
  }
  return linhas;
}

/** Micros como número de planilha, em reais com centavos. */
export function valorNaPlanilha(micros: number | null): number | null {
  return dinheiro(micros);
}

/**
 * Entrega o arquivo ao navegador, como `.xlsx`.
 *
 * `aba` é o nome que aparece na aba do Excel. `revokeObjectURL` no fim: sem
 * ele o conteúdo fica preso na memória da aba até ela fechar — e quem exporta
 * a base algumas vezes na mesma sessão acumula cada uma delas.
 */
export function baixar(tabela: Tabela, nome: string, aba = 'Planilha'): void {
  const blob = new Blob([xlsx(tabela, aba)], { type: TIPO_XLSX });
  const url = URL.createObjectURL(blob);
  const elo = document.createElement('a');
  elo.href = url;
  elo.download = nome;
  document.body.append(elo);
  elo.click();
  elo.remove();
  URL.revokeObjectURL(url);
}
